/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2021 Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import PostStream from 'flarum/forum/components/PostStream';
import CommentPost from 'flarum/forum/components/CommentPost';
import PostMeta from 'flarum/forum/components/PostMeta';
import PostUser from 'flarum/forum/components/PostUser';
import Button from 'flarum/common/components/Button';
import ArticleRating from '../components/ArticleRating';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import listItems from 'flarum/common/helpers/listItems';
import tagsLabel from 'flarum/tags/helpers/tagsLabel';
import Link from 'flarum/common/components/Link';
import avatar from 'flarum/common/helpers/avatar';
import userOnline from 'flarum/common/helpers/userOnline';
import ItemList from 'flarum/common/utils/ItemList';
import type Mithril from 'mithril';
import type Discussion from 'flarum/common/models/Discussion';
import type Review from '../models/Review';

export default function () {
	extend(PostStream.prototype, 'view', function (vnode) {
		const review = this.discussion.review();
		if (review) {
			try {
				customizePostStream(vnode, review, this.discussion);
			} catch (e) {
				// A stream that renders unadorned beats one that does not render.
			}
		}
	});

	extend(PostUser.prototype, 'view', function (vnode) {
		const { isReview, post } = this.attrs;

		if (!isReview) {
			return;
		}

		const user = post.user();

		if (!user) return;

		const children = vnode.children;

		// `children` is a broad union; only an array can be indexed, and only an object vnode
		// carries a `children` of its own.
		if (!Array.isArray(children)) return;

		const firstChild = children[0];

		if (firstChild && typeof firstChild === 'object' && !Array.isArray(firstChild) && firstChild.children) {
			children[0] = (
				<h3>
					<Link href={app.route.user(user)}>
						{avatar(user, { className: 'PostUser-avatar' })}
						{app.translator.trans('glowingblue-orell-rezensionen.forum.reviews.from_text', { user })}
						{userOnline(user)}
					</Link>
				</h3>
			);
		}
	});

	extend(CommentPost.prototype, 'elementAttrs', function (attrs) {
		const { post } = this.attrs;
		const review = post.discussion().review();
		if (review) {
			attrs.className =
				(attrs.className || '') + ' ReviewPost' + (post === post.discussion().firstPost() ? ' ReviewPost--first' : ' ReviewPost--comment');
		}
	});
}

const customizePostStream = (vnode: Mithril.Vnode, review: Review, discussion: Discussion) => {
	// The post stream always renders an array of vnodes. This stays the same array the stream
	// renders, so the `splice()` calls below apply in place.
	let children = vnode.children as Mithril.Vnode<any>[];

	let lastComment = 0;
	children = children.map((item, i) => {
		if (item.attrs['data-type'] === 'comment' && item.attrs['data-number'] === 1) {
			item.attrs.className += ' PostStream-initial';
		}
		if (item.attrs['data-type'] === 'comment' && item.attrs['data-number'] > 1) {
			item.attrs.className += ' PostStream-comment';
			lastComment = i;
		}
		if (item.attrs['data-type'] !== 'comment') {
			item.attrs.className += ' PostStream-other';
		}
		return item;
	});
	vnode.children = children;

	if (lastComment) {
		children[lastComment].attrs.className += ' PostStream-comment-last';
	}

	const headerItems = new ItemList<Mithril.Children>();

	headerItems.add('meta', [<ArticleRating value={review.rating()} />, <PostMeta post={discussion.firstPost()} />]);

	app.cardVisible = false;

	const tags = discussion.tags();
	headerItems.add('reviewHeader', [
		<span>{tags && tags.length && tagsLabel(tags, { link: false })}</span>,
		<h2>{discussion.title()}</h2>,
		<PostUser post={discussion.firstPost()} isReview={true} cardVisible={false} oncardshow={() => {}} oncardhide={() => {}} />,
	]);

	const header = (
		<header className="Post-header">
			<ul>{listItems(headerItems.toArray())}</ul>
		</header>
	);

	children.splice(0, 0, header);

	const comment = () => {
		// `forceRefresh` is not optional in core's typings, though the call relies on its default.
		(DiscussionControls.replyAction as (goToLast: boolean) => Promise<void>).call(discussion, true);
	};
	const commentAction = (
		<div className="ReviewPage-comment_action">
			<Button className="Button Button--large Button--action" onclick={comment}>
				{app.translator.trans('glowingblue-orell-rezensionen.forum.review_page.comment')}
			</Button>
		</div>
	);
	children.splice(children.length > 2 ? 2 : 1, 0, commentAction);

	// A review discussion always has its own first post, so the count is set.
	const commentCount = discussion.commentCount()! - 1;
	if (commentCount > 0) {
		const commentCountElement = (
			<div className="ReviewPage-comment_count">
				{app.translator.trans('glowingblue-orell-rezensionen.forum.review_page.comments', {
					comments: commentCount,
				})}
			</div>
		);
		children.splice(3, 0, commentCountElement);
	}
};
